'use client';
import { useLanguage } from '@bells/core/i18n';
import {
  attendantTitle,
  homeHeroBecomeVip,
  homeHeroExplore,
} from '@bells/i18n';
import { baseLocale } from '@bells/i18n/runtime';
import { type AttendantSearchResponse } from '@bells/schema/models/attendant';
import { chunk, first } from 'lodash';
import Image from 'next/image';
import Link from 'next/link';
import React, { useMemo } from 'react';
import { Button } from '../../../components/button';
import type { HeroTitleType } from './hero-title.types';
import LocationSearchHero from './location-search';

const ScrollColumn = ({
  attendants,
  animationName,
  duration,
  delay,
}: {
  attendants: AttendantSearchResponse[];
  duration: number;
  delay: number;
  animationName: string;
}) => (
  <div
    className={`flex gap-4 lg:flex-col ${animationName}`}
    style={{ animationDuration: `${duration}s`, animationDelay: `${delay}s` }}
  >
    {attendants.map((attendant, i) => {
      const pickLocation = useMemo(() => {
        return first(attendant.locations);
      }, [attendant.locations]);

      return (
        <div
          key={i}
          className='relative w-[35vw] shrink-0 overflow-hidden rounded-xl bg-muted pt-[12%] sm:w-[30vw] sm:pt-[12%] md:w-[22vw] md:pt-[10%] lg:h-auto lg:w-full lg:pb-[105%]'
        >
          <Image
            placeholder={
              attendant.defaultMedia?.blurThumbnail ? 'blur' : undefined
            }
            blurDataURL={attendant.defaultMedia?.blurThumbnail ?? undefined}
            src={attendant.defaultMedia?.xs ?? ''}
            alt={
              pickLocation
                ? attendantTitle({
                    name: attendant.name,
                    city: pickLocation.name,
                  })
                : attendant.name
            }
            fill
            loading={'lazy'}
            unoptimized
            className={
              'h-full w-full object-cover transition-transform duration-500 hover:scale-105'
            }
          />

          <div className='absolute bottom-4 left-4 z-[1] text-xs font-bold text-white lg:bottom-6 lg:left-6 lg:text-base'>
            {attendant.name}, {attendant.age}
          </div>
        </div>
      );
    })}
  </div>
);

const HeroContent = React.forwardRef<
  HTMLDivElement,
  HeroTitleType & {
    attendants?: AttendantSearchResponse[];
    className?: string;
  }
>(({ title, description, attendants }, ref) => {
  const attantendsArray = attendants ?? [];
  const chunks = chunk(attantendsArray, Math.ceil(attantendsArray.length / 2));
  const left = chunks[0] || [];
  const right = chunks[1] || [];

  const { language } = useLanguage();
  return (
    <div
      ref={ref}
      className='relative isolate z-[2] flex w-full flex-col justify-center overflow-hidden pt-[58px] lg:justify-start'
    >
      <div className='relative mx-auto grid w-full max-w-screen-xl grid-cols-1 gap-4 py-6 lg:grid-cols-[1fr_40%] lg:gap-12 lg:py-0'>
        <div className='order-2 my-6 flex w-full flex-col items-center justify-center px-4 text-start lg:order-1 lg:my-24 lg:max-w-screen-sm lg:items-start lg:gap-4 lg:px-8'>
          <p className='word-break text-brand pb-2 text-center text-xs font-extrabold tracking-wide lg:text-left lg:text-sm'>
            BB Escort
          </p>
          <h1 className='text-center text-4xl font-bold tracking-wide lg:text-left lg:text-5xl lg:leading-tight lg:tracking-tight lg:text-foreground'>
            {title}
          </h1>
          <p className='text-paragraph mt-4 w-full text-center text-base lg:text-left lg:text-lg'>
            {description}
          </p>
          <LocationSearchHero className='z-[15] mt-8'></LocationSearchHero>

          <div className='mt-6 flex flex-wrap items-center justify-center gap-2 lg:gap-4'>
            <Button size={'xl'} className='rounded-full' asChild>
              <Link
                href={language === 'de' ? '/escort-damen' : '/en/escort-girls'}
              >
                {homeHeroExplore()}
              </Link>
            </Button>
            <Button
              className='rounded-full'
              size={'xl'}
              asChild
              variant={'outline'}
            >
              <Link
                href={
                  baseLocale != language
                    ? `/${language}/auth/signin/customer`
                    : `/auth/signin/customer`
                }
              >
                {homeHeroBecomeVip()}
              </Link>
            </Button>
          </div>
        </div>
        <div className='fade-mask-bottom relative order-1 w-full lg:order-2 lg:mt-0 lg:h-full lg:px-8 lg:pt-0'>
          <div className='flex h-full w-full gap-4 lg:absolute lg:right-0 lg:top-0 lg:grid lg:max-w-[500px] lg:grid-cols-2 lg:items-end lg:justify-end lg:px-8 lg:pr-0'>
            <ScrollColumn
              attendants={left}
              animationName='scroll'
              duration={30}
              delay={0}
            />
            <ScrollColumn
              attendants={right}
              animationName='scroll'
              duration={20}
              delay={-15}
            />
          </div>
        </div>
      </div>

      <style jsx global>
        {`
          .fade-mask-bottom {
            -webkit-mask-image: linear-gradient(
              to right,
              transparent 0%,
              black 20%,
              black 80%,
              transparent 100%
            );
            mask-image: linear-gradient(
              to right,
              transparent 0%,
              black 20%,
              black 80%,
              a transparent 100%
            );
            -webkit-mask-size: 100% 100%;
            mask-size: 100% 100%;
            -webkit-mask-repeat: no-repeat;
            mask-repeat: no-repeat;
            pointer-events: none;
          }

          @keyframes scrollY {
            0% {
              transform: translateY(0%);
            }
            100% {
              transform: translateY(-50%);
            }
          }

          @keyframes scrollX {
            0% {
              transform: translateX(0%);
            }
            100% {
              transform: translateX(-50%);
            }
          }

          .scroll {
            animation: scrollX 10s linear infinite;
            display: flex;
          }

          @media (min-width: 1024px) {
            .scroll {
              animation: scrollY 10s linear infinite;
              display: flex;
              flex-direction: column;
            }

            .fade-mask-bottom {
              -webkit-mask-image: linear-gradient(
                to bottom,
                transparent 0%,
                black 20%,
                black 80%,
                transparent 100%
              );
              mask-image: linear-gradient(
                to bottom,
                transparent 0%,
                black 20%,
                black 80%,
                transparent 100%
              );
              -webkit-mask-size: 100% 100%;
              mask-size: 100% 100%;
              -webkit-mask-repeat: no-repeat;
              mask-repeat: no-repeat;
              pointer-events: none;
            }
          }
        `}
      </style>
    </div>
  );
});
HeroContent.displayName = 'HeroContent';
export { HeroContent };
